import { useCallback, useEffect, useMemo, useState } from "react";
import { SortableHeader, TableSortResetBar, sortRowsByKey, useTableSort } from "@/components/dataTable";
import type { AutocompleteOption } from "@/components/Autocomplete";
import { Card } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { Alert } from "@/components/ui/Alert";
import { Spinner } from "@/components/ui/Spinner";
import { TextInput } from "@/components/ui/Field";
import { DataTableScrollShell } from "@/components/dataTable/DataTableScrollShell";
import { LockedAutocomplete } from "@/components/LockedAutocomplete";
import { listKontaktKunden, type Geschaeftspartner } from "@/lib/kontakte";
import { heroArticleOrderText, type HeroArticleSuggestion } from "@/lib/material";
import type { HeroCustomer } from "../../lib/settingsApi";
import {
  deleteHourlyRate,
  deleteTravelKmRate,
  getHeroProductByNr,
  listHourlyRates,
  listTravelKmRates,
  upsertHourlyRate,
  upsertTravelKmRate,
  type RateMapOut,
} from "../../lib/settingsApi";
import { appCustomerSource, heroCustomerOption, materialArticleSource } from "../../lib/sources";

interface EditableRateRow {
  key: string;
  id: number | null;
  kunden_id: number | null;
  hero_nr: string;
  artikel_id: number | null;
  customer: AutocompleteOption<HeroCustomer> | null;
  material: AutocompleteOption<HeroArticleSuggestion> | null;
  productInfo: string;
}

function buildMaterialOption(
  heroNr: string,
  artikelId: number | null,
  articleName?: string | null,
): AutocompleteOption<HeroArticleSuggestion> {
  const nr = heroNr.trim();
  const name = (articleName || "").trim() || nr;
  return {
    key: nr,
    label: name,
    description: name !== nr && nr ? `Artikelnummer ${nr}` : undefined,
    value: {
      label: name,
      value: nr,
      hero_nr: nr,
      artikel_id: artikelId,
      hero_unit: null,
      einheit: null,
      ean: null,
    },
  };
}

function appCustomerNames(gp: Geschaeftspartner): {
  company: string | null;
  first: string | null;
  last: string | null;
} | null {
  if (gp.typ !== "kunde" || gp.is_deleted) return null;
  const company = (gp.firmenname || "").trim() || null;
  const first = (gp.vorname || "").trim() || null;
  const last = (gp.nachname || "").trim() || null;
  if (!company && !first && !last) return null;
  return { company, first, last };
}

/** Zeigt den App-Kunden (Geschäftspartner), nicht die Kundencode-Bezeichnung. */
async function overlayAppCustomerNames(entries: RateMapOut[]): Promise<RateMapOut[]> {
  const ids = [
    ...new Set(entries.map((entry) => entry.kunden_id).filter((id): id is number => id != null)),
  ];
  const resolved = new Map<number, { company: string | null; first: string | null; last: string | null }>();
  await Promise.all(
    ids.map(async (id) => {
      try {
        const listed = await listKontaktKunden({ hero_contact_id: id, limit: 5 });
        const byHero = (listed.items || []).find((item) => item.hero_contact_id === id);
        const names = byHero ? appCustomerNames(byHero) : null;
        if (names) resolved.set(id, names);
      } catch {
        // API-Anzeigename behalten
      }
    }),
  );
  if (resolved.size === 0) return entries;
  return entries.map((entry) => {
    if (entry.kunden_id == null) return entry;
    const names = resolved.get(entry.kunden_id);
    if (!names) return entry;
    const label = names.company || [names.first, names.last].filter(Boolean).join(" ");
    return {
      ...entry,
      kunden_label: label,
      kunden_company_name: names.company,
      kunden_first_name: names.first,
      kunden_last_name: names.last,
    };
  });
}

function customerOptionFromRate(entry: RateMapOut): AutocompleteOption<HeroCustomer> | null {
  if (entry.kunden_id == null) return null;
  const company = (entry.kunden_company_name || "").trim() || null;
  const first = (entry.kunden_first_name || "").trim() || null;
  const last = (entry.kunden_last_name || "").trim() || null;
  const label = (entry.kunden_label || "").trim();
  // Firmenname des App-Kunden. kunden_label ist nur Fallback, sonst gewinnt
  // die Kundencode-Bezeichnung („Verschiedene“) gegen den echten Kunden.
  return heroCustomerOption({
    id: entry.kunden_id,
    company_name: company || (!first && !last ? label : null) || null,
    first_name: first,
    last_name: last,
  });
}

function toEditable(entries: RateMapOut[]): EditableRateRow[] {
  const sorted = [...entries].sort((a, b) => {
    if (a.kunden_id == null) return -1;
    if (b.kunden_id == null) return 1;
    return 0;
  });
  if (!sorted.some((e) => e.kunden_id == null)) {
    sorted.unshift({ id: null, kunden_id: null, hero_nr: "", artikel_id: null });
  }
  return sorted.map((e, i) => {
    const articleName = (e.artikel_name || "").trim() || null;
    const articleUnit = (e.artikel_unit || "").trim() || null;
    const productInfo =
      articleName && articleUnit
        ? `${articleName} (${articleUnit})`
        : articleName || "";
    return {
      key: e.id != null ? String(e.id) : `new-${i}`,
      id: e.id,
      kunden_id: e.kunden_id,
      hero_nr: e.hero_nr || "",
      artikel_id: e.artikel_id ?? null,
      customer: customerOptionFromRate(e),
      material: e.hero_nr
        ? buildMaterialOption(e.hero_nr, e.artikel_id ?? null, articleName)
        : null,
      productInfo,
    };
  });
}

function RateMappingSection({
  title,
  description,
  articleLabel,
  articleSearchPlaceholder,
  hideManualArticleNr = false,
  load,
  save,
  remove,
}: {
  title: string;
  description: string;
  articleLabel: string;
  articleSearchPlaceholder: string;
  hideManualArticleNr?: boolean;
  load: () => Promise<RateMapOut[]>;
  save: (body: { kunden_id: number | null; hero_nr: string; artikel_id?: number | null }) => Promise<unknown>;
  remove: (id: number) => Promise<void>;
}) {
  const [rows, setRows] = useState<EditableRateRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { sort, toggle, reset } = useTableSort();
  const sortedRows = useMemo(
    () =>
      sortRowsByKey(rows, sort, (row, key) =>
        key === "customer" ? row.customer?.label || (row.kunden_id == null ? "Standard" : "") : row.hero_nr,
      ),
    [rows, sort],
  );

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const entries = await overlayAppCustomerNames(await load());
      setRows(toEditable(entries));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Laden fehlgeschlagen.");
    } finally {
      setLoading(false);
    }
  }, [load]);

  useEffect(() => {
    reload();
  }, [reload]);

  function updateRow(key: string, patch: Partial<EditableRateRow>) {
    setRows((prev) => prev.map((r) => (r.key === key ? { ...r, ...patch } : r)));
  }

  async function refreshProductInfo(key: string, heroNr: string) {
    if (!heroNr.trim()) {
      updateRow(key, { productInfo: "", material: null });
      return;
    }
    const info = await getHeroProductByNr(heroNr);
    if (!info?.name) {
      updateRow(key, { productInfo: "" });
      return;
    }
    setRows((prev) =>
      prev.map((r) => {
        if (r.key !== key) return r;
        const productInfo = info.unit ? `${info.name} (${info.unit})` : info.name || "";
        return {
          ...r,
          productInfo,
          material: buildMaterialOption(heroNr, r.artikel_id, info.name),
        };
      }),
    );
  }

  function addRow() {
    setRows((prev) => [
      ...prev,
      {
        key: `new-${Date.now()}`,
        id: null,
        kunden_id: null,
        hero_nr: "",
        artikel_id: null,
        customer: null,
        material: null,
        productInfo: "",
      },
    ]);
  }

  async function handleSaveAll() {
    setSaving(true);
    setError(null);
    try {
      for (const row of rows) {
        const hero_nr = row.material?.value?.hero_nr || row.hero_nr.trim();
        if (!hero_nr) continue;
        const kunden_id = row.kunden_id;
        if (kunden_id == null && rows.indexOf(row) !== 0) continue;
        await save({
          kunden_id: rows.indexOf(row) === 0 ? null : kunden_id,
          hero_nr,
          artikel_id: row.material?.value?.artikel_id ?? row.artikel_id,
        });
      }
      await reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Speichern fehlgeschlagen.");
    } finally {
      setSaving(false);
    }
  }

  async function handleDeleteRow(row: EditableRateRow, index: number) {
    if (index === 0) return;
    if (row.id) {
      try {
        await remove(row.id);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Löschen fehlgeschlagen.");
        return;
      }
    }
    setRows((prev) => prev.filter((r) => r.key !== row.key));
  }

  return (
    <Card
      title={title}
      description={description}
      actions={
        <div className="flex gap-2">
          <Button type="button" variant="secondary" onClick={addRow}>
            Zeile hinzufügen
          </Button>
          <Button type="button" onClick={handleSaveAll} loading={saving}>
            Speichern
          </Button>
        </div>
      }
    >
      {loading && (
        <div className="flex items-center gap-2 text-sm text-label-secondary">
          <Spinner /> Lädt …
        </div>
      )}
      {error && <Alert variant="error" title="Fehler" className="mb-3">{error}</Alert>}
      {!loading && (
        <>
        <TableSortResetBar visible={sort != null} onReset={reset} />
        <DataTableScrollShell
          syncKey={`${title}|${rows.length}|${rows.map((r) => r.key).join(",")}`}
          head={
            <tr>
              <th className="eh-data-th px-3 py-2 text-left"><SortableHeader label="Kunde" columnKey="customer" sort={sort} onSort={toggle} /></th>
              <th className="eh-data-th px-3 py-2 text-left"><SortableHeader label={articleLabel} columnKey="article" sort={sort} onSort={toggle} /></th>
              <th className="eh-data-th px-3 py-2 text-right w-16" />
            </tr>
          }
        >
          <tbody className="divide-y divide-separator">
              {sortedRows.map((row) => {
                const index = rows.indexOf(row);
                return (
                <tr key={row.key}>
                  <td className="px-3 py-2 align-top">
                    {index === 0 ? (
                      <strong>Standard</strong>
                    ) : (
                      <LockedAutocomplete<HeroCustomer>
                        placeholder="Kunde suchen …"
                        value={row.customer}
                        minChars={1}
                        emptyText="Keine Kunden gefunden"
                        loadOptions={appCustomerSource}
                        onChange={(opt) => {
                          updateRow(row.key, {
                            customer: opt,
                            kunden_id: opt?.value?.id ?? null,
                          });
                        }}
                      />
                    )}
                  </td>
                  <td className="px-3 py-2 align-top space-y-2">
                    {!hideManualArticleNr ? (
                      <TextInput
                        value={row.hero_nr}
                        onChange={(e) => {
                          const v = e.target.value;
                          updateRow(row.key, { hero_nr: v });
                          void refreshProductInfo(row.key, v);
                        }}
                        placeholder="Artikelnummer"
                      />
                    ) : null}
                    <LockedAutocomplete<HeroArticleSuggestion>
                      placeholder={articleSearchPlaceholder}
                      value={row.material}
                      minChars={2}
                      loadOptions={materialArticleSource}
                      onChange={(opt) => {
                        if (!opt) {
                          updateRow(row.key, {
                            material: null,
                            hero_nr: "",
                            artikel_id: null,
                            productInfo: "",
                          });
                          return;
                        }
                        const heroNr = opt.value?.hero_nr || "";
                        const name = heroArticleOrderText(opt.value || { label: opt.label });
                        const unit = (opt.value?.einheit || opt.value?.hero_unit || "").trim();
                        updateRow(row.key, {
                          material: heroNr
                            ? buildMaterialOption(heroNr, opt.value?.artikel_id ?? null, name)
                            : { ...opt, label: name },
                          hero_nr: heroNr,
                          artikel_id: opt.value?.artikel_id ?? null,
                          productInfo: name ? (unit ? `${name} (${unit})` : name) : "",
                        });
                      }}
                    />
                    {row.productInfo && (
                      <p className="text-xs text-label-secondary">{row.productInfo}</p>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right align-top">
                    {index > 0 && (
                      <Button
                        type="button"
                        size="sm"
                        variant="danger"
                        onClick={() => handleDeleteRow(row, index)}
                      >
                        ×
                      </Button>
                    )}
                  </td>
                </tr>
              )})}
            </tbody>
        </DataTableScrollShell>
        </>
      )}
    </Card>
  );
}

export function HourlyRatesSection() {
  return (
    <RateMappingSection
      title="Monteur-Stundensätze je Kunde"
      description="Standard-Stundensatz und kundenspezifische Artikel. Kunden und Artikel kommen aus der lokalen App-Datenbank."
      articleLabel="Artikel (Stundensatz)"
      articleSearchPlaceholder="Artikel suchen …"
      hideManualArticleNr
      load={listHourlyRates}
      save={upsertHourlyRate}
      remove={deleteHourlyRate}
    />
  );
}

export function TravelKmSection() {
  return (
    <RateMappingSection
      title="Fahrtkilometer-Artikel je Kunde"
      description="Standard-Artikel und kundenspezifische Fahrtkilometer-Zuordnung. Kunden kommen aus der lokalen App-Datenbank."
      articleLabel="Artikel (Fahrtkilometer)"
      articleSearchPlaceholder="Artikel suchen …"
      load={listTravelKmRates}
      save={upsertTravelKmRate}
      remove={deleteTravelKmRate}
    />
  );
}
